import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BrowserCompatWrapper } from '@/components/ui/BrowserCompatWrapper';
import { useNotifyFiscalizacion } from '../hooks/useMunicipalListMutations';
import { getApiErrorStatus } from '../utils/api-error.utils';
import { countPendingNotification } from '../utils/status-transitions.utils';
import { preRegistrationRoutes } from '../utils/routes.utils';
import type { MunicipalListDetail } from '../types/pre-registration.types';

interface NotifyFiscalizacionButtonProps {
  list: MunicipalListDetail;
}

// Sends one email to Fiscalización with the rows of the list not notified yet
export const NotifyFiscalizacionButton: React.FC<NotifyFiscalizacionButtonProps> = ({ list }) => {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const notify = useNotifyFiscalizacion(list.id);
  const pending = countPendingNotification(list.accommodations);

  const confirm = () => {
    setConfirmOpen(false);
    notify.mutate(undefined, {
      onError: (error) => {
        if (getApiErrorStatus(error) === 404) navigate(preRegistrationRoutes.municipalLists);
      },
    });
  };

  const button = (
    <Button
      variant="outline"
      disabled={pending === 0 || notify.isPending}
      onClick={() => setConfirmOpen(true)}
    >
      {notify.isPending ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <Mail className="mr-2 h-4 w-4" />
      )}
      Notificar a Fiscalización ({pending})
    </Button>
  );

  return (
    <BrowserCompatWrapper>
      {pending === 0 ? (
        <Tooltip>
          {/* Disabled buttons do not fire pointer events; the span keeps the tooltip working */}
          <TooltipTrigger asChild>
            <span tabIndex={0}>{button}</span>
          </TooltipTrigger>
          <TooltipContent>No hay alojamientos nuevos para notificar</TooltipContent>
        </Tooltip>
      ) : (
        button
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Notificar a Fiscalización</AlertDialogTitle>
            <AlertDialogDescription>
              Se enviará un correo a Fiscalización con {pending}{' '}
              {pending === 1 ? 'alojamiento' : 'alojamientos'} de {list.municipality.name} que
              todavía no fueron notificados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirm}>Enviar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </BrowserCompatWrapper>
  );
};
